import { useQuery } from '@tanstack/react-query';
import React from 'react';

import envVars from '../../../../../constants/envVars';
import { useUserStatusContext } from '../../../../../context/UserStatus';
import useRequest from '../../../../../hooks/useRequest';
import { ApiEnvelope } from '../../../types';
import { drChronoTaskStatusesMock } from '../mockData';
import { DrChronoTaskStatus } from '../types';
import { DRCHRONO_TASK_STATUSES_KEY } from './constants';

const HOUR = 60 * 60 * 1000;

const NOT_CONFIGURED: ApiEnvelope<DrChronoTaskStatus[]> = {
    status: 'success',
    message: '',
    data: []
};

/**
 * Org-wide, cached 24 h by rtm-api. An org without a DrChrono task setup (which is also when
 * tasks cannot be created) gets a 400 `drchrono_tasks_not_configured`; that is read as no
 * statuses so it caches like data — a cached error would be refetched on every mount.
 */
const useGetDrChronoTaskStatuses = () => {
    const req = useRequest();
    const { activeOrgId } = useUserStatusContext();

    const request = React.useCallback(() => {
        if (envVars.USE_EHR_MOCK_DATA) {
            return Promise.resolve(drChronoTaskStatusesMock);
        }

        return req<ApiEnvelope<DrChronoTaskStatus[]>>({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/drchrono-task-statuses/`
        }).catch((error: ApiEnvelope<unknown> | undefined) =>
            error?.message === 'drchrono_tasks_not_configured'
                ? NOT_CONFIGURED
                : Promise.reject(error)
        );
    }, [req]);

    // Keyed by org because `useRequest` scopes the call to the active org.
    return useQuery({
        queryKey: [DRCHRONO_TASK_STATUSES_KEY, activeOrgId],
        queryFn: request,
        select: (response) => response.data,
        staleTime: HOUR,
        gcTime: HOUR
    });
};

export default useGetDrChronoTaskStatuses;
